import React, { useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import Header from '../pages/Header';
import Home from '../pages/Home';
import PrivateRoute from '../auth/PrivateRoute';

const Dashboard = lazy(() => import('../pages/Dashboard'));
const Landing = lazy(() => import('../pages/Landing'));
const Login = lazy(() => import('../auth/Login'));
const Signup = lazy(() => import('../auth/Signup'));
const ForgotPassword = lazy(() => import('../auth/ForgotPassword'));
const ResetPassword = lazy(() => import('../auth/ResetPassword'));
const Terms = lazy(() => import('../pages/Terms'));
const Privacy = lazy(() => import('../pages/Privacy'));
const SetupGuide = lazy(() => import('../pages/SetupGuide'));
const FAQ = lazy(() => import('../pages/FAQ'));
const Blog = lazy(() => import('../pages/Blog'));
const BlogPost = lazy(() => import('../pages/BlogPost'));
const Account = lazy(() => import('../pages/Account'));
const OrgDashboard = lazy(() => import('../pages/OrgDashboard'));
const MenuExtractor = lazy(() => import('../pages/MenuExtractor'));
const NotFound = lazy(() => import('../pages/NotFound'));
import AuthProvider from '../auth/AuthProvider';
import { useSettings } from '../../hooks/useSettings';
import { useCurrentAdmin } from '../../context/AuthContext';
import { ThemeProvider } from '../../context/ThemeContext';
import { ToastProvider } from '../../context/ToastContext';
import ErrorBoundary from './ErrorBoundary';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Applies dynamic theme colors from restaurant settings (only when authenticated)
const ThemeStyleProvider = ({ children }) => {
  const currentAdmin = useCurrentAdmin();
  const { data: settings } = useSettings({ enabled: !!currentAdmin });

  const themeStyles = {
    '--primary-color': settings?.primaryColor || '#D4AF37',
    '--accent-color': settings?.accentColor || '#B87333',
    '--background-color': settings?.backgroundColor || '#1C1C1E',
    '--text-color': settings?.textColor || '#F2F0ED',
    '--primary-font': settings?.primaryFont || 'Inter, system-ui, sans-serif',
  };

  return <div style={themeStyles}>{children}</div>;
};

const AppLayout = () => {
  const [menuState, setMenuState] = useState(false);
  const [tourKey, setTourKey] = useState(0);
  const location = useLocation();
  const isPublicMenu = location.pathname.startsWith('/menu/');

  const handleStartTour = () => setTourKey(k => k + 1);

  return (
    <ThemeStyleProvider>
      <a href="#main-content" className="skip-to-content">Skip to content</a>
      {!isPublicMenu && <Header menuState={menuState} setMenuState={setMenuState} onStartTour={handleStartTour} />}
      <main id="main-content">
      <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/menu/:slug" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Dashboard tourKey={tourKey} />
            </PrivateRoute>
          }
        />
        <Route
          path="/account"
          element={
            <PrivateRoute>
              <Account />
            </PrivateRoute>
          }
        />
        <Route
          path="/org-dashboard"
          element={
            <PrivateRoute>
              <OrgDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/org-dashboard/extract"
          element={
            <PrivateRoute>
              <MenuExtractor />
            </PrivateRoute>
          }
        />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/setup-guide" element={<SetupGuide />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/settings" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      </main>
    </ThemeStyleProvider>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <ToastProvider>
              <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <AuthProvider>
                  <AppLayout />
                </AuthProvider>
              </Router>
            </ToastProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
